import { Box, Typography, useTheme } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface StatBoxProps {
  icon?: React.ReactNode;
  title: string;
  value: string | number;
  change: number;
  isPositive: boolean;
  trendData?: number[];
}

export const StatBox = ({
  icon,
  title,
  value,
  change,
  isPositive,
  trendData = [3, 2, 4, 1, 5, 3, 4],
}: StatBoxProps) => {
  const theme = useTheme();
  const sparklineOptions: Highcharts.Options = {
    chart: {
      type: 'line',
      height: 50,
      width: 150,
      backgroundColor: 'transparent',
      margin: [2, 0, 2, 0],
    },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: {
      visible: false,
    },
    yAxis: {
      visible: false,
    },
    legend: { enabled: false },
    plotOptions: {
      line: {
        color: theme.palette.primary.main,
        lineWidth: 2,
        marker: { enabled: false },
      },
    },
    series: [
      {
        type: 'line',
        data: trendData,
      },
    ],
    tooltip: { enabled: false },
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '10px',
        p: 2,
        minWidth: { xs: '100%', sm: '300px' },
        border: theme.borders.borderLight,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>{icon && <Box>{icon}</Box>}</Box>
      <Typography variant="subtitle1">{title}</Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
        }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 1, fontWeight: theme.typography.h2.fontWeight }}>
            {value}
          </Typography>
          <Typography
            sx={{
              backgroundColor:
                change === 0
                  ? theme.palette.secondary.main
                  : isPositive
                    ? theme.palette.success.light
                    : theme.palette.error.light,
              color:
                change === 0
                  ? theme.typography.subtitle1.color
                  : isPositive
                    ? theme.palette.success.main
                    : theme.palette.error.main,
              padding: '4px 8px',
              borderRadius: '16px',
              display: 'inline-block',
              fontWeight: theme.typography.h1.fontWeight,
            }}
          >
            {change === 0 ? '+0.00' : `${change}`}%
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            ml: { xs: 0, sm: 2 },
            mt: { xs: 2, sm: 0 },
            width: '100%',
            maxWidth: { xs: '100%', sm: '150px' },
          }}
        >
          <HighchartsReact highcharts={Highcharts} options={sparklineOptions} />
        </Box>
      </Box>
    </Box>
  );
};
